import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@fluentui/react-components";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  setCurrentStep,
  toggleDetailId,
  setSelectedDetailIds,
  toggleExpectationSelection,
  setSelectedExpectationIds,
  setActualAmount,
  fetchStatementDetail,
  fetchMoreDetailRows,
  fetchExpectationsPage,
  StatementDetail,
  ExpectationRecord,
} from "../../../redux/Commission/commissionStatementSlice";

// ─── Colors ───────────────────────────────────────────────────────────────────
const NAVY  = "#205393";
const RED   = "#c92a2a";
const T1    = "#0f172a";
const T2    = "#475569";
const T3    = "#94a3b8";
const GRN   = "#2b8a3e";
const GRNL  = "#ebf9f0";
const ORA   = "#c05621";
const BRD   = "#e2e5ea";
const BRD2  = "#c8cdd5";
const SUR1  = "#fafbfd";
const SUR2  = "#f0f2f4";
const SEL_BG = "rgba(32,83,147,0.07)";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtAmt = (v: string | number | null | undefined): string => {
  const n = parseFloat(String(v ?? ""));
  return isNaN(n) ? "—" : `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDate = (d: string | null | undefined): string =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// ─── Icons ────────────────────────────────────────────────────────────────────
const SplitIcon = () => (
  <div style={{ width: 22, height: 22, display: "flex", gap: 3, padding: 3, background: NAVY, borderRadius: 5, flexShrink: 0 }}>
    <div style={{ flex: 1, background: "#fff", borderRadius: 2 }} />
    <div style={{ flex: 1, background: "#fff", borderRadius: 2 }} />
  </div>
);

// ─── View toggle ──────────────────────────────────────────────────────────────
type ViewMode = "individual" | "grouped";
const ViewToggle: React.FC<{ view: ViewMode; onChange: (v: ViewMode) => void }> = ({ view, onChange }) => (
  <div style={{ display: "flex", background: SUR2, border: `1px solid ${BRD2}`, borderRadius: 6, padding: 2, gap: 2 }}>
    {(["individual", "grouped"] as const).map((k) => (
      <button key={k} onClick={() => onChange(k)} style={{
        height: 28, padding: "0 14px", border: "none", borderRadius: 4,
        background: view === k ? "#1e293b" : "transparent",
        color: view === k ? "#fff" : T2,
        fontSize: 12, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap",
      }}>
        {k === "individual" ? "Individual rows" : "Grouped by case"}
      </button>
    ))}
  </div>
);

// ─── Badge ────────────────────────────────────────────────────────────────────
const Badge: React.FC<{ type: "blue" | "green"; children: React.ReactNode }> = ({ type, children }) => (
  <span style={{
    padding: "2px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap",
    background: type === "blue" ? "rgba(32,83,147,0.12)" : GRNL,
    color: type === "blue" ? NAVY : GRN,
  }}>{children}</span>
);

// ─── Table header styles ──────────────────────────────────────────────────────
const TH: React.CSSProperties = {
  background: SUR2, borderBottom: `1px solid ${BRD}`, padding: "8px 12px",
  fontSize: 10.5, fontWeight: 700, color: T3, whiteSpace: "nowrap",
  position: "sticky", top: 0, zIndex: 1, letterSpacing: "0.05em", textTransform: "uppercase",
  textAlign: "left",
};
const TH_R: React.CSSProperties = { ...TH, textAlign: "right" };

// ─── Virtual row window ───────────────────────────────────────────────────────
// ponytail: fixed-height slice; upgrade to @tanstack/react-virtual if rows grow
//           past VIRTUAL_THRESHOLD frequently or row heights become variable.
const VIRTUAL_ROW_H = 37;
const VIRTUAL_OVERSCAN = 8;
const VIRTUAL_THRESHOLD = 150;

function useVirtualRows<T>(items: T[]) {
  const [scrollTop, setScrollTop] = useState(0);
  const rafRef = useRef<number>(0);
  useEffect(() => () => { cancelAnimationFrame(rafRef.current); }, []);
  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const top = (e.currentTarget as HTMLDivElement).scrollTop;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setScrollTop(top));
  }, []);
  if (items.length <= VIRTUAL_THRESHOLD) {
    return { onScroll, visibleItems: items, topPad: 0, bottomPad: 0 };
  }
  const panelH = typeof window !== "undefined" ? Math.max(280, window.innerHeight - 420) : 480;
  const start = Math.max(0, Math.floor(scrollTop / VIRTUAL_ROW_H) - VIRTUAL_OVERSCAN);
  const end = Math.min(items.length, Math.ceil((scrollTop + panelH) / VIRTUAL_ROW_H) + VIRTUAL_OVERSCAN);
  return { onScroll, visibleItems: items.slice(start, end), topPad: start * VIRTUAL_ROW_H, bottomPad: (items.length - end) * VIRTUAL_ROW_H };
}

// ─── Component ────────────────────────────────────────────────────────────────
const StepValidateMatch: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const userId = useSelector((s: RootState) => s.authUser?.userId ?? "");
  const {
    detailRows, detailHasMore, loadingMoreDetail,
    expectations, expectationHasMore, loadingMoreExpectations,
    selectedDetailIds, selectedExpectationIds, actualAmounts,
    statementId, providerId, startDate, endDate,
    loadingDetail, loading, error,
  } = useSelector((s: RootState) => s.commissionStatement);

  const [viewMode,   setViewMode]   = useState<ViewMode>("grouped");
  const [hoveredRef, setHoveredRef] = useState<string | null>(null);

  // Page tracking refs for scroll pagination
  const detailPageRef      = useRef(1);
  const expectationPageRef = useRef(1);
  const leftSentinelRef    = useRef<HTMLDivElement | null>(null);
  const rightSentinelRef   = useRef<HTMLDivElement | null>(null);

  // Reset page refs when statement changes
  useEffect(() => {
    detailPageRef.current = 1;
    expectationPageRef.current = 1;
  }, [statementId]);

  // Load detail rows (page 1) on mount / statement change
  useEffect(() => {
    if (!statementId) return;
    dispatch(fetchStatementDetail(statementId));
  }, [statementId]);

  // Load expectations (page 1) — separate effect so userId is a real dependency
  useEffect(() => {
    if (!statementId || !userId) return;
    expectationPageRef.current = 1;
    dispatch(fetchExpectationsPage({ userId, startDate, endDate, providerId, page: 1 }));
  }, [statementId, userId]);

  // Left panel scroll pagination
  useEffect(() => {
    const el = leftSentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && detailHasMore && !loadingMoreDetail && statementId) {
        detailPageRef.current += 1;
        dispatch(fetchMoreDetailRows({ statementId, page: detailPageRef.current }));
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [detailHasMore, loadingMoreDetail, statementId]);

  // Right panel scroll pagination
  useEffect(() => {
    const el = rightSentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && expectationHasMore && !loadingMoreExpectations && statementId && userId) {
        expectationPageRef.current += 1;
        dispatch(fetchExpectationsPage({ userId, startDate, endDate, providerId, page: expectationPageRef.current }));
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [expectationHasMore, loadingMoreExpectations, statementId, userId]);

  // ── Derived ──────────────────────────────────────────────────────────────

  const activeDetailRows = useMemo(
    () => detailRows.filter((r) => !r.Processed),
    [detailRows],
  );

  const leftTotal = useMemo(
    () => activeDetailRows
      .filter((r) => selectedDetailIds.includes(r.CommissionStatementDetailId))
      .reduce((s, r) => s + (parseFloat(r.ActualAmount || "0") || 0), 0),
    [activeDetailRows, selectedDetailIds]
  );

  const activeExpectations = useMemo(
    () => expectations.filter((e) => e.CommissionReceiptId === null),
    [expectations],
  );

  const selectedExpAmt = useMemo(() => {
    return Array.from(selectedExpectationIds).reduce((s, id) => {
      const exp = activeExpectations.find((e) => e.CommissionExpectationId === id);
      if (!exp) return s;
      const raw = actualAmounts[id] ?? exp.ExpectedAmount ?? "0";
      return s + (parseFloat(raw) || 0);
    }, 0);
  }, [selectedExpectationIds, expectations, actualAmounts]);


  // ── Groups ───────────────────────────────────────────────────────────────
  const leftGroups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, StatementDetail[]>();
    for (const r of activeDetailRows) {
      const k = r.CaseRef || "(blank)";
      if (!map.has(k)) { map.set(k, []); order.push(k); }
      map.get(k)!.push(r);
    }
    return order.map((k) => ({ caseRef: k, rows: map.get(k)! }));
  }, [activeDetailRows]);

  const rightGroups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, ExpectationRecord[]>();
    for (const e of activeExpectations) {
      const k = e.CaseRef || "(blank)";
      if (!map.has(k)) { map.set(k, []); order.push(k); }
      map.get(k)!.push(e);
    }
    return order.map((k) => ({ caseRef: k, exps: map.get(k)! }));
  }, [expectations]);

  // ── Group toggle helpers ──────────────────────────────────────────────────
  const toggleLeftGroup = (rows: StatementDetail[]) => {
    const ids    = rows.map((r) => r.CommissionStatementDetailId);
    const allSel = ids.every((id) => selectedDetailIds.includes(id));
    if (allSel) {
      dispatch(setSelectedDetailIds(selectedDetailIds.filter((id) => !ids.includes(id))));
    } else {
      const toAdd = ids.filter((id) => !selectedDetailIds.includes(id));
      dispatch(setSelectedDetailIds([...selectedDetailIds, ...toAdd]));
    }
  };

  const toggleRightGroup = (exps: ExpectationRecord[]) => {
    const ids    = exps.filter((e) => e.CommissionReceiptId === null).map((e) => e.CommissionExpectationId);
    const allSel = ids.every((id) => selectedExpectationIds.has(id));
    const next   = new Set(selectedExpectationIds);
    if (allSel) ids.forEach((id) => next.delete(id)); else ids.forEach((id) => next.add(id));
    dispatch(setSelectedExpectationIds(Array.from(next)));
  };

  // ── Row styles ────────────────────────────────────────────────────────────
  const rStyle = (caseRef: string, isSel: boolean): React.CSSProperties => {
    const active = isSel || hoveredRef === caseRef;
    return {
      boxShadow: active ? `inset 4px 0 0 ${NAVY}` : "inset 4px 0 0 transparent",
      background: active ? SEL_BG : undefined,
      transition: "background 0.1s, box-shadow 0.1s",
    };
  };
  const subStyle = (caseRef: string, anyActive: boolean): React.CSSProperties => {
    const active = anyActive || hoveredRef === caseRef;
    return {
      boxShadow: active ? `inset 4px 0 0 ${NAVY}` : "inset 4px 0 0 transparent",
      background: active ? "rgba(32,83,147,0.04)" : SUR2,
    };
  };

  // ── Left row ─────────────────────────────────────────────────────────────
  const renderLeftRow = (row: StatementDetail, i: number) => {
    const isSel = selectedDetailIds.includes(row.CommissionStatementDetailId);
    return (
      <tr key={`l-${row.CommissionStatementDetailId}-${i}`}
        style={{ ...rStyle(row.CaseRef, isSel), borderBottom: `1px solid ${BRD}`, cursor: "pointer" }}
        onMouseEnter={() => setHoveredRef(row.CaseRef)}
        onMouseLeave={() => setHoveredRef(null)}
        onClick={() => dispatch(toggleDetailId(row.CommissionStatementDetailId))}
      >
        <td style={{ padding: "8px 12px", width: 40 }}>
          <input type="checkbox" checked={isSel}
            onChange={() => dispatch(toggleDetailId(row.CommissionStatementDetailId))}
            onClick={(e) => e.stopPropagation()}
            style={{ accentColor: NAVY, cursor: "pointer" }} />
        </td>
        <td style={{ padding: "8px 12px", fontSize: 12, color: T2, textAlign: "right", fontFamily: "monospace" }}>{row.RowNumber}</td>
        <td style={{ padding: "8px 12px", fontWeight: 600, color: NAVY, fontSize: 12.5, fontFamily: "monospace" }}>
          {row.CaseRef || <em style={{ color: T3 }}>(blank)</em>}
        </td>
        <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "monospace", fontWeight: 600, fontSize: 12.5, color: T1 }}>
          {fmtAmt(row.ActualAmount)}
        </td>
        <td style={{ padding: "8px 12px", fontSize: 12, color: T2 }}>{row.ClientNameFromFile || "—"}</td>
      </tr>
    );
  };

  // ── Right row ─────────────────────────────────────────────────────────────
  const renderRightRow = (exp: ExpectationRecord) => {
    const isSel  = selectedExpectationIds.has(exp.CommissionExpectationId);
    const edited = actualAmounts[exp.CommissionExpectationId] ?? exp.ExpectedAmount ?? "";
    return (
      <tr key={`r-${exp.CommissionExpectationId}`}
        style={{ ...rStyle(exp.CaseRef, isSel), borderBottom: `1px solid ${BRD}`, cursor: "pointer" }}
        onMouseEnter={() => setHoveredRef(exp.CaseRef)}
        onMouseLeave={() => setHoveredRef(null)}
        onClick={() => dispatch(toggleExpectationSelection(exp.CommissionExpectationId))}
      >
        <td style={{ padding: "8px 12px", width: 40 }}>
          <input type="checkbox" checked={isSel}
            onChange={() => dispatch(toggleExpectationSelection(exp.CommissionExpectationId))}
            onClick={(e) => e.stopPropagation()}
            style={{ accentColor: NAVY, cursor: "pointer" }} />
        </td>
        <td style={{ padding: "8px 12px", fontWeight: 600, color: NAVY, fontSize: 12.5, fontFamily: "monospace" }}>
          {exp.CaseRef || "—"}
        </td>
        <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "monospace", fontSize: 12.5, color: T2 }}>
          {fmtAmt(exp.ExpectedAmount)}
        </td>
        <td style={{ padding: "8px 12px", textAlign: "right" }}>
          <input type="number" step="0.01" min="0" value={edited} placeholder="Enter amount"
            onChange={(e) => dispatch(setActualAmount({ id: exp.CommissionExpectationId, amount: e.target.value }))}
            onClick={(e) => e.stopPropagation()}
            style={{ width: 104, height: 26, padding: "0 8px", border: `1px solid ${BRD2}`, borderRadius: 4, fontSize: 12, fontFamily: "monospace", textAlign: "right", outline: "none", background: "#fff" }} />
        </td>
        <td style={{ padding: "8px 12px", fontSize: 12, color: T2 }}>{fmtDate(exp.DueDate)}</td>
        <td style={{ padding: "8px 12px", fontSize: 12, color: T2 }}>{exp.ClientNameDb || "—"}</td>
      </tr>
    );
  };

  // ── Left subtotal ─────────────────────────────────────────────────────────
  const renderLeftSub = (g: { caseRef: string; rows: StatementDetail[] }, isLast: boolean) => {
    const anyActive = g.rows.some((r) => selectedDetailIds.includes(r.CommissionStatementDetailId));
    const total     = g.rows.reduce((s, r) => s + (parseFloat(r.ActualAmount || "0") || 0), 0);
    return (
      <React.Fragment key={`ls-${g.caseRef}`}>
        <tr style={{ ...subStyle(g.caseRef, anyActive), cursor: "pointer" }}
          onMouseEnter={() => setHoveredRef(g.caseRef)} onMouseLeave={() => setHoveredRef(null)}
          onClick={() => toggleLeftGroup(g.rows)}
        >
          <td style={{ padding: "5px 12px", borderBottom: `2px solid ${BRD}` }} />
          <td style={{ padding: "5px 12px", borderBottom: `2px solid ${BRD}` }} />
          <td style={{ padding: "5px 12px", fontSize: 11.5, color: T3, fontStyle: "italic", borderBottom: `2px solid ${BRD}` }}>Subtotal</td>
          <td style={{ padding: "5px 12px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, fontSize: 12.5, color: T1, borderBottom: `2px solid ${BRD}` }}>
            {`£${total.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </td>
          <td style={{ borderBottom: `2px solid ${BRD}` }} />
        </tr>
        {!isLast && <tr><td colSpan={5} style={{ height: 8, background: SUR1 }} /></tr>}
      </React.Fragment>
    );
  };

  // ── Right subtotal ────────────────────────────────────────────────────────
  const renderRightSub = (g: { caseRef: string; exps: ExpectationRecord[] }, isLast: boolean) => {
    const anyActive = g.exps.some((e) => selectedExpectationIds.has(e.CommissionExpectationId));
    const expTotal  = g.exps.reduce((s, e) => s + (parseFloat(e.ExpectedAmount || "0") || 0), 0);
    const actTotal  = g.exps.reduce((s, e) => {
      const raw = actualAmounts[e.CommissionExpectationId] ?? e.ExpectedAmount ?? "0";
      return s + (parseFloat(raw) || 0);
    }, 0);
    return (
      <React.Fragment key={`rs-${g.caseRef}`}>
        <tr style={{ ...subStyle(g.caseRef, anyActive), cursor: "pointer" }}
          onMouseEnter={() => setHoveredRef(g.caseRef)} onMouseLeave={() => setHoveredRef(null)}
          onClick={() => toggleRightGroup(g.exps)}
        >
          <td style={{ padding: "5px 12px", borderBottom: `2px solid ${BRD}` }} />
          <td style={{ padding: "5px 12px", fontSize: 11.5, color: T3, fontStyle: "italic", borderBottom: `2px solid ${BRD}` }}>Subtotal</td>
          <td style={{ padding: "5px 12px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, fontSize: 12.5, color: T1, borderBottom: `2px solid ${BRD}` }}>
            {fmtAmt(expTotal.toFixed(2))}
          </td>
          <td style={{ padding: "5px 12px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, fontSize: 12.5, color: actTotal !== expTotal ? ORA : T1, borderBottom: `2px solid ${BRD}` }}>
            {fmtAmt(actTotal.toFixed(2))}
          </td>
          <td colSpan={2} style={{ borderBottom: `2px solid ${BRD}` }} />
        </tr>
        {!isLast && <tr><td colSpan={6} style={{ height: 8, background: SUR1 }} /></tr>}
      </React.Fragment>
    );
  };

  const canProceed = selectedDetailIds.length > 0 && selectedExpectationIds.size > 0;

  const leftVirtual = useVirtualRows(activeDetailRows);
  const rightVirtual = useVirtualRows(activeExpectations);

  const PANEL_H = "calc(100vh - 420px)";
  const PANEL: React.CSSProperties = {
    flex: 1, minWidth: 0, border: `1px solid ${BRD}`, borderRadius: 6,
    overflow: "hidden", display: "flex", flexDirection: "column", background: "#fff",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  };

  if (loadingDetail) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, gap: 10 }}>
        <Spinner size="small" />
        <span style={{ fontSize: 13, color: T2 }}>Loading payment records…</span>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Title row */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <SplitIcon />
        <span style={{ fontSize: 15, fontWeight: 700, color: T1, flex: 1 }}>
          Payments &amp; Expectations Reconciliation
        </span>
        <div style={{ background: "rgba(255,200,0,0.18)", border: "1px solid rgba(200,150,0,0.3)", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#7a4f00", fontWeight: 500 }}>
          Hover any row to cross-highlight
        </div>
        <ViewToggle view={viewMode} onChange={setViewMode} />
      </div>

      {/* Dual panels */}
      <div style={{ display: "flex", gap: 12 }}>

        {/* LEFT — Payment Records */}
        <div style={PANEL}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderBottom: `1px solid ${BRD}`, background: SUR1 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T1 }}>Payment Records</span>
            <Badge type="blue">{selectedDetailIds.length} / {activeDetailRows.length} marked</Badge>
            <span style={{ marginLeft: "auto", fontSize: 12, color: T2 }}>{fmtAmt(leftTotal.toFixed(2))} selected</span>
          </div>
          <div style={{ overflowY: "auto", overflowX: "auto", flex: 1, minHeight: 280, maxHeight: PANEL_H }} onScroll={leftVirtual.onScroll}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
              <thead>
                <tr>
                  <th style={TH}>Proc.</th>
                  <th style={TH_R}>Row</th>
                  <th style={TH}>Case Reference</th>
                  <th style={TH_R}>Amount</th>
                  <th style={TH}>Client (file)</th>
                </tr>
              </thead>
              <tbody>
                {activeDetailRows.length === 0 ? (
                  error.detail ? (
                    <tr>
                      <td colSpan={5} style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#ffe6e6", border: `1px solid ${RED}`, borderRadius: 5, fontSize: 12, color: RED }}>
                          <span style={{ flex: 1 }}>Failed to load payment records. Please try again.</span>
                          <button
                            onClick={() => statementId && dispatch(fetchStatementDetail(statementId))}
                            style={{ background: RED, color: "#fff", border: "none", borderRadius: 4, fontSize: 11.5, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}
                          >Retry</button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr><td colSpan={5} style={{ textAlign: "center", padding: 32, color: T3, fontSize: 12 }}>No payment records.</td></tr>
                  )
                ) : viewMode === "individual" ? (
                  <>
                    {leftVirtual.topPad > 0 && <tr><td colSpan={5} style={{ height: leftVirtual.topPad, padding: 0 }} /></tr>}
                    {leftVirtual.visibleItems.map((r, i) => renderLeftRow(r, i))}
                    {leftVirtual.bottomPad > 0 && <tr><td colSpan={5} style={{ height: leftVirtual.bottomPad, padding: 0 }} /></tr>}
                  </>
                ) : (
                  leftGroups.map((g, gi) => (
                    <React.Fragment key={`lg-${g.caseRef}`}>
                      {g.rows.map((r, i) => renderLeftRow(r, i))}
                      {renderLeftSub(g, gi === leftGroups.length - 1)}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
            {/* Scroll sentinel + load-more indicator */}
            <div ref={leftSentinelRef} style={{ height: 1 }} />
            {loadingMoreDetail && (
              <div style={{ display: "flex", justifyContent: "center", padding: "10px 0" }}>
                <Spinner size="tiny" />
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Expectations */}
        <div style={PANEL}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderBottom: `1px solid ${BRD}`, background: SUR1 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T1 }}>Expectations</span>
            <Badge type="green">{selectedExpectationIds.size} / {activeExpectations.length} selected</Badge>
            <span style={{ marginLeft: "auto", fontSize: 12, color: T2 }}>{fmtAmt(selectedExpAmt.toFixed(2))} selected</span>
          </div>
          <div style={{ overflowY: "auto", overflowX: "auto", flex: 1, minHeight: 280, maxHeight: PANEL_H }} onScroll={rightVirtual.onScroll}>
            {loading.expectations ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 120, gap: 8 }}>
                <Spinner size="tiny" />
                <span style={{ fontSize: 12, color: T2 }}>Loading expectations…</span>
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
                <thead>
                  <tr>
                    <th style={TH}>Recv.</th>
                    <th style={TH}>Case Reference</th>
                    <th style={TH_R}>Expected Amt</th>
                    <th style={TH_R}>Actual Amt</th>
                    <th style={TH}>Due Date</th>
                    <th style={TH}>Client (DB)</th>
                  </tr>
                </thead>
                <tbody>
                  {activeExpectations.length === 0 ? (
                    error.expectations ? (
                      <tr>
                        <td colSpan={6} style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#ffe6e6", border: `1px solid ${RED}`, borderRadius: 5, fontSize: 12, color: RED }}>
                            <span style={{ flex: 1 }}>Failed to load expectations. Please try again.</span>
                            <button
                              onClick={() => { expectationPageRef.current = 1; dispatch(fetchExpectationsPage({ userId, startDate, endDate, providerId, page: 1 })); }}
                              style={{ background: RED, color: "#fff", border: "none", borderRadius: 4, fontSize: 11.5, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}
                            >Retry</button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: T3, fontSize: 12 }}>
                        No expectations found for this provider and date range.
                      </td></tr>
                    )
                  ) : viewMode === "individual" ? (
                    <>
                      {rightVirtual.topPad > 0 && <tr><td colSpan={6} style={{ height: rightVirtual.topPad, padding: 0 }} /></tr>}
                      {rightVirtual.visibleItems.map((e) => renderRightRow(e))}
                      {rightVirtual.bottomPad > 0 && <tr><td colSpan={6} style={{ height: rightVirtual.bottomPad, padding: 0 }} /></tr>}
                    </>
                  ) : (
                    rightGroups.map((g, gi) => (
                      <React.Fragment key={`rg-${g.caseRef}`}>
                        {g.exps.map((e) => renderRightRow(e))}
                        {renderRightSub(g, gi === rightGroups.length - 1)}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            )}
            {/* Scroll sentinel + load-more indicator */}
            <div ref={rightSentinelRef} style={{ height: 1 }} />
            {loadingMoreExpectations && (
              <div style={{ display: "flex", justifyContent: "center", padding: "10px 0" }}>
                <Spinner size="tiny" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Validation banner */}
      {selectedExpectationIds.size > 0 && (
        <div style={{ marginTop: 10, border: `1px solid #aed6b8`, borderRadius: 5, padding: "8px 14px", background: GRNL, fontSize: 12.5, color: GRN }}>
          <strong>
            {selectedDetailIds.length} payment row{selectedDetailIds.length !== 1 ? "s" : ""} marked ·{" "}
            {selectedExpectationIds.size} expectation{selectedExpectationIds.size !== 1 ? "s" : ""} selected
          </strong>
        </div>
      )}

      {/* Buttons */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${BRD}` }}>
        <button onClick={() => dispatch(setCurrentStep(2))}
          style={{ background: "#fff", color: T2, border: `1px solid ${BRD2}`, borderRadius: 5, fontSize: 12.5, fontWeight: 500, padding: "0 14px", height: 32, cursor: "pointer" }}>
          ← Back
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={() => canProceed && dispatch(setCurrentStep(4))} disabled={!canProceed}
          style={{ background: canProceed ? NAVY : "#c8cdd5", color: "#fff", border: "none", borderRadius: 5, fontSize: 12.5, fontWeight: 500, padding: "0 18px", height: 32, cursor: canProceed ? "pointer" : "not-allowed" }}>
          Review &amp; Apply →
        </button>
      </div>
    </div>
  );
};

export default StepValidateMatch;
